/**
 * Server-side RBAC. Permissions are DERIVED FROM ROLE in code — never persisted
 * per-user (that was a legacy bug). Mirrors the capability table in
 * docs/PROJECT_BRIEF.md §5.2.
 */

export const ROLES = ["admin", "manager", "accountant", "viewer"] as const;
export type Role = (typeof ROLES)[number];

export const PERMISSIONS = [
  "records:read",
  "records:create",
  "records:update",
  "records:delete",
  "dashboard:view",
  "analytics:view",
  "settings:manage",
  "users:manage",
  "deleted:manage",
  "supplier_cost:view", // field-level: see supplier cost / supplier name
] as const;
export type Permission = (typeof PERMISSIONS)[number];

export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  admin: [...PERMISSIONS],
  manager: [
    "records:read",
    "records:create",
    "dashboard:view",
    "analytics:view",
    "supplier_cost:view",
  ],
  // accountant = read-only PI/Invoice/Statement (enforced further at resource level)
  accountant: ["records:read"],
  viewer: ["records:read", "dashboard:view"],
};

export function hasPermission(role: Role, perm: Permission): boolean {
  return ROLE_PERMISSIONS[role]?.includes(perm) ?? false;
}

/** Resources an accountant may read (others are denied even for read). */
/**
 * Kế toán đọc được những tài nguyên nào.
 *
 * Ba cái đầu là công việc của họ. Hai cái sau là ĐIỀU KIỆN để ba cái đầu dùng
 * được, không phải nới lỏng:
 *  - `customers`: màn Bảng công nợ có ô lọc theo khách; chặn thì ô đó rỗng và
 *    kế toán không lọc được sổ của mình.
 *  - `companies`: PI và hoá đơn hiển thị pháp nhân thu tiền; chặn thì cột đó
 *    trống trên chính chứng từ họ được xem.
 *
 * Danh sách công ty ở route `GET /companies` chỉ trả id + tên. Số tài khoản
 * ngân hàng nằm ở `GET /companies/detail`, vẫn gác `settings:manage` riêng —
 * kế toán KHÔNG đọc được.
 */
export const ACCOUNTANT_READABLE = new Set([
  "proforma-invoices",
  "invoices",
  "statements",
  "customers",
  "companies",
]);

/**
 * Vai trò này có được ĐỌC tài nguyên đó không — cùng một quy tắc mà
 * `requireResourceRead` áp ở máy chủ.
 *
 * Đặt ở `shared` để sidebar, command palette và route guard dùng CHUNG với
 * máy chủ. Trước đây giao diện không lọc gì cả: mọi vai trò thấy đủ 28 mục
 * menu, kế toán bấm vào Sản phẩm thì dựng xong cả trang rồi mới nhận 403.
 *
 * Đây là tiện ích hiển thị, KHÔNG phải hàng rào an ninh — máy chủ vẫn gác độc
 * lập. Ẩn một mục menu chỉ để người dùng khỏi đâm vào tường.
 */
export function canReadResource(role: Role, resource: string): boolean {
  if (!hasPermission(role, "records:read")) return false;
  if (role === "accountant") return ACCOUNTANT_READABLE.has(resource);
  return true;
}

/**
 * Trang đầu tiên vai trò này thực sự mở được.
 *
 * "/" là Dashboard, cần `dashboard:view` — mà kế toán KHÔNG có. Nên kế toán
 * đăng nhập xong là rơi thẳng vào một màn hình lỗi. Đưa họ tới thứ họ được
 * xem thay vì bắt tự mò.
 */
export function landingPathFor(role: Role, portalSupplierId?: string | null): string {
  if (portalSupplierId) return "/portal";
  if (hasPermission(role, "dashboard:view")) return "/";
  if (canReadResource(role, "proforma-invoices")) return "/proforma-invoices";
  return "/settings";
}
